<script lang="ts" setup>
import { onClickOutside, useLocalStorage } from '@vueuse/core'
import { computed, provide, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteRecordRaw, useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth-store'
import { resolveRouteTitle } from '@/utils/resolve-route-title'

import Copyright from '@/components/layout/Copyright.vue'
import SidebarNavigation from '@/components/layout/navigation/SidebarNavigation.vue'
import PageNavigationLink from '@/components/layout/navigation/PageNavigationLink.vue'
import LocaleDropdown from '@/components/LocaleDropdown.vue'
import ProfileDropdown from '@/components/auth/ProfileDropdown.vue'

const route = useRoute()
const authStore = useAuthStore()
const { t } = useI18n()

const isSidebarOpen = ref(false)
const isPageNavigationOpen = ref(false)
const navBar = ref<HTMLElement | null>(null)
const isSidebarCollapsed = useLocalStorage('sidebarCollapsed', false)

provide('sidebarCollapsed', isSidebarCollapsed)

function toggleSidebarCollapse() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const topRoute = computed<RouteRecordRaw>(() => route.matched[0])
const routeChildren = computed(() => {
    const routes = topRoute.value?.children ?? []

    return routes.filter((route) => {
        if (route.meta?.hideInNavigation) return false

        const requiredLicense = route.meta?.requiredLicense
        if (requiredLicense && !authStore.hasLicense(requiredLicense)) {
            return false
        }

        if (route.name === 'dm.deviations' && authStore.company?.options.hideDeliveryDeviations) {
            return false
        }

        return true
    })
})

function closeSidebar() {
    isSidebarOpen.value = false
}
function toggleSidebar() {
    isSidebarOpen.value = !isSidebarOpen.value
}

function closePageNavigation() {
    isPageNavigationOpen.value = false
}
function togglePageNavigation() {
    isPageNavigationOpen.value = !isPageNavigationOpen.value
}

onClickOutside(navBar, closePageNavigation)
</script>

<template>
    <div class="flex h-screen w-full max-w-full overflow-hidden bg-primary-100 dark:bg-primary-600">
        <!-- Sidebar Navigation -->
        <SidebarNavigation
            :is-open="isSidebarOpen"
            :is-collapsed="isSidebarCollapsed"
            @close="closeSidebar"
            @toggle-collapse="toggleSidebarCollapse"
        />

        <!-- Main Content Area -->
        <div
            class="flex flex-1 flex-col min-w-0 transition-all duration-300"
            :class="{ 'lg:ml-64': !isSidebarCollapsed, 'lg:ml-16': isSidebarCollapsed }"
        >
            <!-- Mobile Header -->
            <header
                class="flex h-12 items-center justify-between bg-primary-100 dark:bg-dark-700 px-2 shadow-sm lg:hidden"
            >
                <button class="text-gray-500 hover:text-gray-700" @click="toggleSidebar">
                    <MdiMenu class="h-6 w-6" />
                </button>
                <div class="flex items-center gap-2">
                    <LocaleDropdown />
                    <ProfileDropdown theme="light" />
                </div>
            </header>

            <!-- Desktop Header (always visible) -->
            <header
                class="hidden lg:flex h-10 items-center justify-end bg-primary-100 dark:bg-dark-800 border-b border-gray-200 dark:border-dark-400 px-4 flex-shrink-0"
            >
                <div class="flex items-center gap-3">
                    <LocaleDropdown />
                    <ProfileDropdown theme="light" />
                </div>
            </header>

            <!-- Page Header with Sub-navigation -->
            <div
                v-if="topRoute && !topRoute.meta?.hideHeader"
                class="bg-primary-100 dark:bg-dark-800 flex-shrink-0"
            >
                <div class="px-2 lg:px-3 py-1 relative flex justify-center">
                    <!-- Sub-navigation -->
                    <nav
                        v-if="topRoute.children && topRoute.children.length > 1"
                        ref="navBar"
                        class="border-t border-gray-200 lg:border-t-0 overflow-x-auto"
                    >
                        <!-- Mobile dropdown trigger -->
                        <div class="lg:hidden p-2">
                            <button
                                class="flex w-full items-center justify-between rounded-lg border border-gray-200 dark:border-dark-400 bg-primary-100 dark:bg-dark-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-primary-200 shadow-sm transition hover:bg-gray-50 dark:hover:bg-dark-800 active:bg-gray-100 dark:active:bg-dark-900"
                                @click.prevent="togglePageNavigation"
                            >
                                <span>{{ t(resolveRouteTitle($route)) }}</span>
                                <mdi:chevron-down
                                    :class="{ 'rotate-180': isPageNavigationOpen }"
                                    class="h-5 w-5 transform transition-transform text-gray-500"
                                />
                            </button>
                        </div>

                        <!-- Sub-navigation links -->
                        <div
                            :class="isPageNavigationOpen ? 'flex' : 'hidden'"
                            class="lg:inline-flex lg:flex-row lg:items-center lg:gap-1 lg:bg-gray-200 lg:dark:bg-dark-600 lg:rounded-lg lg:p-1 flex-col px-2 pb-2 text-gray-700"
                        >
                            <PageNavigationLink
                                v-for="child in routeChildren"
                                :key="`PageNavigationLink${child.name as string}`"
                                :to="{
                                    name: child.name,
                                    params: child.meta!.defaultParams ?? {},
                                }"
                                :no-padding="true"
                                :regular-link="true"
                                @click="closePageNavigation"
                                v-text="t(resolveRouteTitle(child))"
                            />
                        </div>
                    </nav>
                </div>
            </div>

            <!-- Main Content -->
            <main class="flex-1 min-w-0 w-full overflow-x-auto overflow-y-auto">
                <div class="w-full min-w-0 max-w-full px-2 py-1 lg:px-3 lg:py-1">
                    <slot />
                </div>
            </main>

            <!-- Footer -->
            <div
                class="border-t border-gray-200 dark:border-dark-400 bg-primary-100 dark:bg-dark-800 flex-shrink-0 py-0.5"
            >
                <Copyright />
            </div>
        </div>
    </div>
</template>
